import { useCallback, useMemo, useState } from "react";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Typography } from "@mui/material";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type { Dayjs, TimeOfDay, TimeRangeOnDate } from "@thalamos/common";
import {
  dayjs,
  formatClockTime,
  formatTimeOfDay,
  ISO_DATE_FORMAT,
  ISO_WEEKDAY,
  mergeTimeRangesOnDate,
  parseTimeOfDay,
  UK_TIME_ZONE,
} from "@thalamos/common";
import { ASSESSMENT_DURATION_MINUTES } from "@thalamos/aspire-common";

import type { UnusableTimeRangeOnDate } from "./requestedAvailability.js";
import {
  formatTimeRangeOnDate,
  unusableRanges,
} from "./requestedAvailability.js";
import type { AvailabilityBlock } from "~/components/design-system/index.js";
import {
  AvailabilityGrid,
  AvailabilityGridLegend,
  blocksToDateRanges,
  buildDatedColumns,
  dateRangesToBlocks,
  formatWeekLabel,
  gridPointForInstant,
  startOfWeekContaining,
} from "~/components/design-system/index.js";

/** Monday first, as a rota is read. */
const WEEK_STARTS_ON = ISO_WEEKDAY.monday;
const DAYS_PER_WEEK = 7;
const MINUTES_PER_HOUR = 60;
/**
 * How many hours of the grid are on screen at once. Passed to the grid rather
 * than left to its default, so the opening scroll can rely on it.
 */
const VISIBLE_HOURS = 6;
/**
 * Recent past kept above the current time when the picker opens, so the edge
 * of the shaded past is visible and reads as "now".
 */
const HOURS_BEFORE_NOW = 1;

export interface RequestedAvailabilityPickerProps {
  /** The whole request, on every week, not only the one on screen. */
  value: TimeRangeOnDate[];
  onChange: (ranges: TimeRangeOnDate[]) => Promisable<void>;
  /**
   * UK wall-clock now: where the picker opens when nothing has been asked for,
   * and the point before which cells are shaded, not locked.
   */
  now: Dayjs;
  "data-testid"?: string;
}

/** What an AMHP has to change about a range before it can be asked for. */
function reasonText(reason: UnusableTimeRangeOnDate["reason"]): string {
  return match(reason)
    .with(
      "short",
      () =>
        `Each time range must be at least ${String(ASSESSMENT_DURATION_MINUTES)} minutes long.`,
    )
    .with(
      "nonexistent",
      () =>
        "This time does not exist on that date, because the clocks go forward.",
    )
    .exhaustive();
}

/** The week the picker opens on: the earliest range's, or the current one. */
function openingWeekStart(
  value: readonly TimeRangeOnDate[],
  now: Dayjs,
): Dayjs {
  const earliest = mergeTimeRangesOnDate(value)[0];
  const anchor =
    earliest === undefined
      ? now
      : dayjs.tz(`${earliest.date} 00:00:00`, UK_TIME_ZONE);
  return startOfWeekContaining(anchor, WEEK_STARTS_ON);
}

function startOfHour(minutesSinceMidnight: number): number {
  return minutesSinceMidnight - (minutesSinceMidnight % MINUTES_PER_HOUR);
}

function clamp(value: number, lower: number, upper: number): number {
  return Math.min(Math.max(value, lower), upper);
}

/** Whether `instant` falls on one of the seven dates the grid is showing. */
function isInWeek(weekStart: Dayjs, instant: Dayjs): boolean {
  const date = instant.format(ISO_DATE_FORMAT);
  return (
    date >= weekStart.format(ISO_DATE_FORMAT) &&
    date <= weekStart.add(DAYS_PER_WEEK - 1, "day").format(ISO_DATE_FORMAT)
  );
}

/**
 * Where the picker opens vertically, as the time to bring to the top.
 *
 * With today on screen, now is always on screen: an hour of shaded past above
 * it shows where now is. The earliest range's start hour is kept on screen too
 * when the two are under `VISIBLE_HOURS` apart, giving up the hour before now
 * if that is what it takes; further apart, the range is left to the list
 * beneath the grid. With today off screen the range's hour decides alone, and
 * `undefined` leaves the grid's own default to stand.
 */
function openingScrollTo(
  value: readonly TimeRangeOnDate[],
  now: Dayjs,
  weekStart: Dayjs,
): TimeOfDay | undefined {
  const earliest = mergeTimeRangesOnDate(value)[0];
  const earliestHour =
    earliest === undefined
      ? undefined
      : startOfHour(parseTimeOfDay(earliest.from));

  if (!isInWeek(weekStart, now)) {
    return earliestHour === undefined
      ? undefined
      : formatTimeOfDay(earliestHour);
  }

  const nowHour = now.hour() * MINUTES_PER_HOUR;
  const preferredTop = Math.max(
    nowHour - HOURS_BEFORE_NOW * MINUTES_PER_HOUR,
    0,
  );
  if (earliestHour === undefined) {
    return formatTimeOfDay(preferredTop);
  }

  // Two hours share a window only when the later starts before the earlier
  // has scrolled off: strictly fewer than VISIBLE_HOURS apart.
  const furthestApart = (VISIBLE_HOURS - 1) * MINUTES_PER_HOUR;
  if (Math.abs(earliestHour - nowHour) > furthestApart) {
    return formatTimeOfDay(preferredTop);
  }
  return formatTimeOfDay(
    clamp(preferredTop, earliestHour - furthestApart, earliestHour),
  );
}

/**
 * The time an AMHP is asking a doctor for, painted a week at a time on the
 * availability grid, with every range they have asked for listed beneath.
 *
 * Controlled and unopinionated about what the request is for, so every place
 * that asks a doctor for time asks the same way.
 */
export function RequestedAvailabilityPicker({
  value,
  onChange,
  now,
  "data-testid": dataTestId = "requested-availability-picker",
}: RequestedAvailabilityPickerProps) {
  // Where the picker opens, not a week it keeps following: painting an earlier
  // week would otherwise yank the view out from under the edit. Re-opening on
  // a replaced value means remounting by `key`.
  const [weekStart, setWeekStart] = useState<Dayjs>(() =>
    openingWeekStart(value, now),
  );
  // Computed once: a value that changed would re-scroll the grid out from
  // under an edit.
  const [initialScrollTo] = useState(() =>
    openingScrollTo(value, now, weekStart),
  );

  const weekStartDate = weekStart.format(ISO_DATE_FORMAT);
  const weekEndDate = weekStart
    .add(DAYS_PER_WEEK - 1, "day")
    .format(ISO_DATE_FORMAT);

  const ranges = useMemo(() => mergeTimeRangesOnDate(value), [value]);
  const unusable = useMemo(() => unusableRanges(value), [value]);

  const unusableStarts = useMemo(
    () =>
      new Set(unusable.map((range) => `${range.date} ${String(range.from)}`)),
    [unusable],
  );

  const reasons = useMemo(
    () => [...new Set(unusable.map(({ reason }) => reasonText(reason)))],
    [unusable],
  );

  const handleChange = useCallback(
    (blocks: AvailabilityBlock[]) => {
      // What is kept is decided by date range rather than by which dates the
      // new blocks mention: a date whose last cell has just been erased is
      // absent from the blocks, and would otherwise keep its old range.
      const elsewhere = value.filter(
        (range) => range.date < weekStartDate || range.date > weekEndDate,
      );
      void onChange(
        mergeTimeRangesOnDate([
          ...elsewhere,
          ...blocksToDateRanges(blocks, weekStart),
        ]),
      );
    },
    [onChange, value, weekStart, weekStartDate, weekEndDate],
  );

  const goToWeek = useCallback((weeks: number) => {
    setWeekStart((current) => current.add(weeks, "week"));
  }, []);

  return (
    <Box data-testid={dataTestId}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
        <IconButton
          size="small"
          aria-label="Previous week"
          onClick={() => {
            goToWeek(-1);
          }}
          data-testid="requested-availability-prev-week"
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle1"
          data-testid="requested-availability-week-label"
          // Wide enough for the longest label (a week spanning two years), so
          // the arrows stay put and can be clicked repeatedly without chasing.
          sx={{ minWidth: "24ch", textAlign: "center" }}
        >
          {formatWeekLabel(weekStart)}
        </Typography>
        <IconButton
          size="small"
          aria-label="Next week"
          onClick={() => {
            goToWeek(1);
          }}
          data-testid="requested-availability-next-week"
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>

      <AvailabilityGrid
        columns={buildDatedColumns(weekStart, { highlightDate: now })}
        value={dateRangesToBlocks(value, weekStart)}
        onChange={handleChange}
        shadeBefore={gridPointForInstant(weekStart, now)}
        flaggedBlocks={dateRangesToBlocks(unusable, weekStart)}
        initialScrollTo={initialScrollTo}
        visibleHours={VISIBLE_HOURS}
      />

      <Box sx={{ mt: 1.5 }}>
        <AvailabilityGridLegend
          swatches={["available", "unavailable", "past"]}
        />
      </Box>

      {reasons.length > 0 && (
        <Box
          sx={{ mt: 1.5 }}
          data-testid="requested-availability-unusable-hint"
        >
          {reasons.map((reason) => (
            <Typography
              key={reason}
              variant="caption"
              component="div"
              sx={{ color: "error.main" }}
            >
              {reason}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 1.5 }}>
        {ranges.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No time requested yet.
          </Typography>
        ) : (
          ranges.map((range) => {
            const flagged = unusableStarts.has(
              `${range.date} ${String(range.from)}`,
            );
            return (
              <Typography
                key={`${range.date} ${String(range.from)}`}
                variant="body2"
                component="div"
                data-testid={`requested-availability-range-${range.date}-${formatClockTime(range.from)}`}
                sx={{ color: flagged ? "error.main" : "text.primary" }}
              >
                {formatTimeRangeOnDate(range)}
              </Typography>
            );
          })
        )}
      </Box>
    </Box>
  );
}
